/**
 * NexusChat URL resolver.
 *
 * REACT_APP_NEXUSCHAT_URL is baked in at docker BUILD time (see the Dockerfile
 * ARG and .github/workflows/ci.yml --build-arg), NOT read at runtime. When it is
 * set we use it verbatim. When it is UNSET we must NEVER fall back to localhost
 * in a deployed bundle — that was the dev-nexus-link bug, where a dev build with
 * no build-arg sent visitors to http://localhost:3080 (Header.jsx SSO-error
 * fallback, LoginPage post-login fallback, DashboardPage "Launch Nex" link).
 *
 * Instead we default BY ENVIRONMENT, derived from the equally build-time
 * REACT_APP_API_URL (dev -> dev-api.videxa.ai, prod -> api.videxa.ai):
 *   - prod  (api.videxa.ai)      -> https://nexus.videxa.ai
 *   - dev   (dev-api.videxa.ai)  -> https://dev-chat.videxa.ai
 *   - local / anything else      -> https://dev-chat.videxa.ai
 *
 * The default is never a localhost URL.
 */

const PROD_NEXUS = 'https://nexus.videxa.ai';
const DEV_NEXUS = 'https://dev-chat.videxa.ai';

const stripTrailingSlash = (url) => url.replace(/\/+$/, '');

/**
 * Resolve the NexusChat base URL.
 * @param {object} [env] Environment object (defaults to process.env; injectable for tests).
 * @returns {string} The NexusChat base URL, with any trailing slash removed.
 */
export const resolveNexusChatUrl = (env = process.env) => {
  const configured = (env.REACT_APP_NEXUSCHAT_URL || '').trim();
  if (configured) return stripTrailingSlash(configured);

  // No explicit build-arg: choose a default from the (build-time) API host.
  // Only the exact prod API authority maps to prod Nexus; everything else
  // (dev-api.videxa.ai, localhost, unset) maps to the dev Nexus host.
  const apiUrl = env.REACT_APP_API_URL || '';
  if (/\/\/api\.videxa\.ai(?:[:/]|$)/i.test(apiUrl)) return PROD_NEXUS;
  return DEV_NEXUS;
};

/**
 * Resolve the NexusChat login page URL — the plain-link fallback used when SSO
 * token generation fails.
 * @param {object} [env] Environment object (defaults to process.env).
 * @returns {string} The NexusChat login URL.
 */
export const resolveNexusChatLoginUrl = (env = process.env) =>
  `${resolveNexusChatUrl(env)}/login`;

export default resolveNexusChatUrl;
